import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Profile {
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  phone: string | null;
  address: string | null;
  postcode: string | null;
  avatar_url: string | null;
}

const EMPTY: Profile = {
  first_name: null,
  last_name: null,
  display_name: null,
  phone: null,
  address: null,
  postcode: null,
  avatar_url: null,
};

interface ProfileContextValue {
  profile: Profile;
  loading: boolean;
  refresh: () => Promise<void>;
  setProfile: (p: Profile) => void;
}

const ProfileContext = createContext<ProfileContextValue>({
  profile: EMPTY,
  loading: false,
  refresh: async () => {},
  setProfile: () => {},
});

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile>(EMPTY);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setProfile(EMPTY);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("profiles")
      .select("first_name, last_name, display_name, phone, address, postcode, avatar_url")
      .eq("user_id", user.id)
      .maybeSingle();
    setProfile({
      first_name: data?.first_name ?? null,
      last_name: data?.last_name ?? null,
      display_name: data?.display_name ?? null,
      phone: data?.phone ?? null,
      address: data?.address ?? null,
      postcode: data?.postcode ?? null,
      avatar_url: data?.avatar_url ?? null,
    });
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <ProfileContext.Provider value={{ profile, loading, refresh, setProfile }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => useContext(ProfileContext);
